import { TeamIdPayload } from 'containers/home/home.model';
import { JOB_ATTRIBUTE_TYPE, JobStatus, LeadSource, PushScheduleBy } from './constant';
import {
  AddressField,
  Customer,
  IInvoiceAttachmentRequest,
  IInvoiceServiceCharge,
  IInvoiceServiceChargeRequest,
  IInvoiceServiceChargeTaxRequest,
  ILineItemRequest,
  IUniqueTaxRequest,
  InvoicePayload,
  InvoiceStatus,
  LineItem
} from 'containers/invoices/invoices.model';
import { DISCOUNT_TYPE } from 'constants/common';
import { API_RESPONSES, AttachmentFile } from 'types/baseTypes';
import { EstimateStatus, UnlinkSourceType } from 'containers/estimates/constants';
import { InvoiceDetails } from 'containers/estimates/estimates.model';
import { ProfitabilitySummaryData } from './components/ProfitabilitySummary';
import { Tag } from 'containers/tags/tags.model';
import { CommissionType } from 'containers/settings/commission-settings/constants';
import { ExpenseTabs } from './job-details/constants';
import { ColumnInfo } from 'containers/accounting/pay-runs/pay-period-review/payperiod.model';
import { StatusIndicator } from 'containers/invoices/constants';

export interface JobListItem {
  id: string;
  title: string;
  status: JobStatus;
  scheduledFrom: string;
  scheduledTo: string;
  customer: JobCustomer; // TODO: REMOVE AFTER api changes
  customerCurrent: JobCustomer;
  customerLatest: JobCustomer;
  assignees: Assignee[];
  jobNumber: string;
  createdAt: string;
  updatedAt: string;
  createdBy: Assignee;
  amount: string;
  jobTags: Tag[];
  isEditable?: boolean;
  serviceAreaName?: string;
  leadSource?: string;
  leadSourceType?: string;
  externalCompanyName?: string;
  invoiceStatus?: InvoiceStatus;
  estimateStatus?: EstimateStatus;
}

export interface Job extends JobListItem {
  teamId: number;
  merchantId: string;
  description: string;
  isScheduled: boolean;
  lineItems: LineItem[];
  leadSourceType: string;
  leadSource: string;
  taxes: Tax[];
  invoices: RelatedInvoiceForJob[];
  createdOn: string;
  serviceCharges: IInvoiceServiceCharge[];
  discountType: string;
  discount: string;
  isServiceFeeEnabled: boolean;
  surchargeAmount: string;
  payableAmount: string;
  billableAmount: string;
  note: { comment: string };
  referenceNo: string;
  attachments: AttachmentFile[];
  scheduledFrom: string;
  scheduledTo: string;
  estimates: RelatedEstimate[];
  externalCompany?: JobExternalCompany;
  externalCompanyId?: string;
  latestInvoice: InvoiceDetails;
  jobTags: Tag[];
  isEditable?: boolean;
  jobServiceArea?: JobServiceArea;
  paymentInfo: PaymentInfo;
  isReferralEditable: boolean;
  suggestedAdjustmentCount?: number;
}

export interface PaymentInfo {
  paymentMethod: string;
  funding: string;
  cardLast4: string;
  network: string;
}

export interface JobServiceArea {
  id: string;
  name: string;
  colorCode: string;
}

export interface JobExternalCompany {
  id: string;
  name: string;
  referralType?: CommissionType | null;
  referralValue?: number | null;
  base?: string | null;
  baseAmount?: number | null;
  referralAmount?: number | null;
  isEdited?: boolean | null;
}

export interface RelatedEstimate {
  id: string;
  estimateNumber: string;
  status: string;
  createdAt: string;
  amount: string;
  title: string;
  hasPermission: boolean;
  createdBy: {
    id: string;
    name: string;
    profilePicture: string;
  };
  expiresAt: string;
}

export interface RelatedInvoiceForJob {
  id: string;
  invoiceNumber: string;
  status: string;
  amountPayable: string;
  createdAt: string;
  createdBy?: Assignee;
  hasPermission?: boolean;
  statusIndicator?: StatusIndicator;
}
export interface Assignee {
  id: string;
  profilePictureURL?: string;
  name: string;
}

interface Tax {
  id: number;
  additionalTaxID: number;
  catalogueTaxId: number;
  name: string;
  percentage: string;
  amount: string;
}
export interface JobCustomer {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  serviceAddress: AddressField[];
  billingAddress: AddressField;
  teamID: number;
  lastName: string;
  billingAddressId: number;
  customerNumber: string;
  companyName: string;
  referenceNumber: string;
  note: string;
  isBillingSameAsService: boolean;
  merchantId: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  address?: AddressField;
  teamId?: number;
  ID?: string;
}

export interface AssignableMembersResponse {
  data: TeamMemberData;
  meta: any;
}

export interface TeamMemberData {
  teamMemberList: AssignableMember[];
}

export interface AssignableMember extends Assignee {
  merchantId: string;
  name: string;
  email: string;
  phone: string;
  profilePictureUrl: string;
  colorCode: string;
}
export interface GetJobsPayload {
  teamId: number;
  createdBy: string[];
  assignees: string[];
  customerIds: string[];
  status: JobStatus[];
  leadSource: string[];
  amount: string;
  scheduledFrom: string;
  scheduledTo: string;
  search: string;
  limit: number;
  page: number;
  viewId?: string;
}

export interface StatusCount {
  status: JobStatus;
  count: number;
}

export interface GetJobsResponse {
  data: {
    count: number;
    jobList: JobListItem[];
    statusCount: StatusCount[];
    columnInfo?: ColumnInfo[];
  };
  meta: any;
}

export interface GetJobByIdResponse {
  data: Job;
}

export interface GetJobByIdPayload {
  teamId: number;
  jobId: string;
  isFromEdit?: boolean;
  isFromInvoice?: boolean;
  isFromEstimate?: boolean;
  isDuplicateJob?: boolean;
}

export interface JobAttachmentType {
  url: string;
  title: string;
  id: string;
  imagePreview: string;
  source?: string;
  urlLink: string;
}

export interface UploadImageResponse {
  data: {
    imageUrl: string;
    url?: string;
  };
  meta: any;
}

export interface CheckJobAttriute extends TeamIdPayload {
  body: { attribute: JOB_ATTRIBUTE_TYPE; value: string };
}

export interface JobAttributeCheckResponse {
  data: {
    isExists: boolean;
    jobNumberList: string[];
  };
  meta: any;
}

export interface CreateJobPayload {
  teamId: string;
  body: CreateJobReqBody;
}

export interface EditJobPayload extends CreateJobPayload {
  jobId: string;
}

export interface CreateJobFromInvoicePayload {
  invoiceId: string;
  teamId: string;
  body: CreateJobFromInvoiceReqBody;
}

export interface CreateJobFromInvoiceReqBody extends CreateJobReqBody {
  invoiceNumber: string;
}

export interface CreateJobReqBody {
  title: string;
  description: string;
  isScheduled: boolean;
  scheduledFrom: string;
  scheduledTo: string;
  jobNote: JobNote;
  leadSourceType: string;
  leadSource: string;
  ReferenceNo: string;
  lineItems: ILineItemRequest[];
  discount: string;
  discountType: DISCOUNT_TYPE;
  surchargeAmount: string;
  isServiceFeeEnabled: boolean;
  attachments: IInvoiceAttachmentRequest[];
  taxes: IInvoiceServiceChargeTaxRequest[];
  serviceCharges: IInvoiceServiceChargeRequest[];
  serviceChargeAmount: string;
  assignees: string[];
  customer: JobCustomer;
  externalCompanyId?: string;
  source: string;
  relativeEntity?: {
    entityType: 'ESTIMATE';
    entityId: string;
    entityNumber: string;
  };
  isAutoFilled: boolean;
  serviceAreaId?: string;
  autofillKey?: string;
  isAutofillModified?: boolean;
  tags?: string[];
  amount: string;
}

export interface JobNote {
  comment: string;
}

export interface JobLeadSourceType {
  id: LeadSource;
  name: string;
}

export interface JobRiskValidationErrorResponse {
  entity: string;
  message: string;
}

export interface JobRiskValidationResponse {
  errors: JobRiskValidationErrorResponse[];
}

export interface CreateJobResponse {
  data: {
    jobId: string;
    jobNumber: string;
    isRiskValidationFailed?: boolean;
    riskValidations?: JobRiskValidationResponse;
  };
  meta: any;
}

export interface UpdateJobResponse {
  data: {
    jobId: string;
    isRiskValidationFailed?: boolean;
    riskValidations?: JobRiskValidationResponse;
  };
  meta: any;
}
export interface EditJobAssigneesPayload {
  assignees: string[];
  teamId: number;
  jobId: string;
}
export interface EditJobAssigneesResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface UpdateJobAssigneesBulkPayload {
  teamId: number;
  body: {
    jobIds: string[];
    isSelectedAll?: boolean;
    excludedJobIds?: string[];
    assignees: string[];
    serviceAreaId?: string;
    isConfirmed?: boolean;
  };
}

export interface UpdateJobAssigneesBulkResponse {
  data: {
    showPopup: boolean;
    noPermissionToEditJobCount: number;
    noPermissionToEditJobList: string[];
    notEditableWithRespectToPayrunJobCount: number;
    notEditableWithRespectToPayrunJobList: string[];
    noPermissionToModifyDoneJobCount: number;
    noPermissionToModifyDoneJobList: string[];
    updateCompleted: boolean;
    updatePossibleJobCount: number;
  };
  meta: any;
}

export interface EditJobStatusResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface EditJobStatusPayload {
  status: JobStatus;
  note: string;
  jobId: string;
  teamId: number;
}

export interface EditJobDescriptionResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface EditJobDescriptionPayload {
  description: string;
  jobId: string;
  teamId: number;
}

export interface EditJobLeadSourceResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface EditJobLeadSourcePayload {
  leadSourceType: string;
  leadSource: string;
  jobId: string;
  teamId: number;
}

export interface EditJobScheduleResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface EditJobSchedulePayload {
  scheduledFrom: string;
  scheduledTo: string;
  jobId: string;
  teamId: number;
}

export interface UpdateJobScheduleBulkPayload {
  teamId: number;
  body: {
    jobIds: string[];
    isSelectedAll?: boolean;
    excludedJobIds?: string[];
    scheduledFrom?: string;
    scheduledTo?: string;
    pushScheduleBy?: PushScheduleBy;
    pushScheduleByValue?: number;
    isConfirmed?: boolean;
  };
}

export interface UpdateJobScheduleBulkResponse {
  data: {
    showPopup: boolean;
    noPermissionToEditJobCount: number;
    noPermissionToEditJobList: string[];
    notEditableWithRespectToPayrunJobCount: number;
    notEditableWithRespectToPayrunJobList: string[];
    noPermissionToModifyDoneJobCount: number;
    noPermissionToModifyDoneJobList: string[];
    noScheduleJobCount: number;
    noScheduleJobList: string[];
    updateCompleted: boolean;
    updatePossibleJobCount: number;
  };
  meta: any;
}

export interface GetJobTimelineResponse {
  data: {
    timelineList: JobTimelineEvent[];
  };
  meta: any;
}

export interface GetJobTimelinePayload {
  jobId: string;
  teamId: number;
  timezone: string;
}

export interface JobTimelineEvent {
  title: string;
  merchantId: string;
  subTitle: string;
  subText: string;
  noteId: number;
  attachments: AttachmentFile[];
  timestamp: string;
  type: JobTimelineEventType;
  subType?: string;
  merchantName?: string;
  merchantProfilePicture?: string;
}

export enum JobTimelineEventType {
  JobAction = 'ACTION',
  Note = 'NOTE',
  JobAttachment = 'JOB_ATTACHMENT',
  JobNote = 'JOB_NOTE'
}

export interface CreateNotePayload {
  teamId: number;
  jobId: string;
  comment: string;
  attachments: AttachmentFile[];
}

export interface CreateNoteResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface EditNotePayload {
  noteId: number;
  teamId: number;
  jobId: string;
  comment: string;
  attachments: AttachmentFile[];
}

export interface EditNoteResponse {
  data: {
    id: number;
    jobId: string;
    note: string;
    hasAttachment: boolean;
    attachments: AttachmentFile[];
  };
}

export interface DeleteNotePayload {
  noteId: number;
  teamId: number;
  jobId: string;
}

export interface DeleteNoteResponse {
  data: {
    message: API_RESPONSES;
  };
}
export interface JobRefNumberResponse {
  data: {
    isExists: boolean;
    jobNumberList: string[];
  };
  meta: any;
}

export interface UnlinkJobAndInvoicePayload {
  teamId: number;
  jobId: string;
  invoiceId: string;
  sourceType: UnlinkSourceType;
}

export interface DeleteJobPayload {
  teamId: number;
  jobId: string;
}

export interface DeleteJobResponse {
  data: {
    message: API_RESPONSES;
  };
}

export interface CreateInvoiceFromJobPayload extends JobTeamIdPayload {
  body: {
    jobInvoiceRequest: InvoicePayload;
    jobNumber: string;
    source: string;
  };
}

export interface CreateInvoiceFromJobResponse {
  data: {
    qrId: string;
    qr: string;
    url: string;
    invoiceId: string;
    customerId: string;
    isNewCustomer: boolean;
    invoiceMessage: string;
    customer: Customer;
    payableAmount: string;
    isDiscountAmountSelected: boolean;
    referenceId: string;
    checkoutURL: string;
    billAmount: string;
    invoiceNumber: string;
  };
  meta: any;
}

export interface JobTeamIdPayload {
  teamId: number;
  jobId: string;
}

export interface JobAttachmentListResponse {
  data: {
    jobAttachmentList: JobAttachmentList;
    jobNoteAttachmentList: JobAttachmentList[];
  };
  meta: any;
}
export interface JobAttachmentList {
  attachmentResponse: AttachmentFile[];
  createdAt: string;
  createdBy: string;
  comment?: string;
}

export interface AutoFillJobDetailPayload {
  content: string;
  timeZone: string;
  attachments: Array<{
    fileKey: string;
    fileType: string;
  }>;
  source: string;
}

export interface AutoFillJobDetailResponse {
  data: {
    id: string;
    teamId: number;
    merchantId: string;
    title: string;
    description: string;
    status: string;
    isScheduled: boolean;
    scheduledFrom: string;
    scheduledTo: string;
    customerCurrent: JobCustomer;
    customerLatest: JobCustomer;
    lineItems: any[];
    assignees: any[];
    jobNumber: string;
    createdAt: string;
    createdBy: {
      id: string;
      profilePictureURL: string;
      name: string;
      phone: string;
    };
    invoices: any[];
    taxes: any[];
    serviceCharges: any[];
    hasDiscount: boolean;
    payableAmount: string;
    billableAmount: string;
    latestInvoice: any;
    note: { comment: string };
    attachments: AttachmentFile[];
    estimates: any[];
    jobTags: any[];
    externalCompany: JobExternalCompany | null;
    isEditable: boolean;
    leadSourceType: string;
    leadSource: string;
    referenceNo: string;
    discount: string;
    discountType: string;
    amount: string;
    isServiceFeeEnabled: boolean;
    serviceChargeAmount: string;
    surchargeAmount: string;
    externalCompanyId: string;
    autofillKey?: string;
    isAnyCustomerData?: boolean;
  };
}

export interface ProfitabilitySummaryResponse {
  data: ProfitabilitySummaryData;
}

export interface EditJobDiscountPayload {
  teamId: number;
  jobId: string;
  body: {
    discount: string;
    discountType: DISCOUNT_TYPE;
    surchargeAmount: string;
    isServiceFeeEnabled: boolean;
  };
}

export interface EditJobServiceChargePayload {
  teamId: number;
  jobId: string;
  body: {
    serviceCharges: IInvoiceServiceChargeRequest[];
    serviceChargeAmount: string;
    taxes?: IUniqueTaxRequest[];
    surchargeAmount: string;
    isServiceFeeEnabled: boolean;
  };
}

export interface EditJobLineItemPayload {
  teamId: number;
  jobId: string;
  body: {
    lineItems: ILineItemRequest[];
    taxes?: IUniqueTaxRequest[];
    surchargeAmount?: string;
    isServiceFeeEnabled?: boolean;
  };
}

export interface UpdateJobTagsPayload {
  teamId: number;
  jobId: string;
  body: {
    tagIds: string[];
  };
}

export interface UpdateJobTagsBulkPayload {
  teamId: number;
  body: {
    tagIds: string[];
    removeExistingTags: boolean;
    jobIds: string[];
    isSelectedAll?: boolean;
    excludedJobIds?: string[];
  };
}

export interface UpdateJobStatusBulkPayload {
  teamId: number;
  body: {
    status: JobStatus;
    jobIds: string[];
    isSelectedAll?: boolean;
    excludedJobIds?: string[];
    isConfirmed?: boolean;
  };
}

export interface UpdateJobStatusBulkResponse {
  data: {
    showPopup: boolean;
    noPermissionToEditJobCount: number;
    noPermissionToEditJobList: string[];
    noPermissionToMarkJobAsDoneCount: number;
    noPermissionToMarkJobAsDoneList: string[];
    notEditableWithRespectToPayrunJobCount: number;
    notEditableWithRespectToPayrunJobList: string[];
    noPermissionToModifyDoneJobCount: number;
    noPermissionToModifyDoneJobList: string[];
    updateCompleted: boolean;
    updatePossibleJobCount: number;
  };
  meta: any;
}

export interface CommissionData {
  merchant: {
    id: string;
    name: string;
    profilePictureURL: string;
  };
  jobId: string;
  teamId: number;
  splitPercentage: string;
  base: string;
  baseAmount: number;
  ruleTitle: string;
  rate: string;
  commissionAmount: string;
  id: number;
  ruleId: string;
  executionType: string;
}

export interface JobCommissionSplitResponse {
  data: CommissionData[];
}

export interface UpdateJobCommissionSplitPayload {
  teamId: number;
  jobId: string;
  body: { data: { merchantId: string; commissionAmount: number; executionType: string }[] };
  commissionData: CommissionData[];
}

export interface ReCalculateJobCommissionSplitResponse {
  merchantId: string;
  executionType: string;
  commissionAmount: string;
  base: string;
  baseAmount: number;
  splitPercentage: string;
  ruleId: string;
  rate: string;
  ruleTitle: string;
}

export interface ReCalculateJobCommissionSplitPayload {
  teamId: number;
  body: { jobId: string; merchantId: string; splitPercentage?: number; defaultSplit: boolean };
}

export interface EditJobExternalCompanyPayload {
  externalCompanyId: string;
  teamId: string;
  jobId: string;
}

export interface EditJobExternalCompanyResponse {
  data: {
    message: string;
  };
}

export interface EditJobExternalCompanyReferralPayload {
  teamId: number;
  jobId: string;
  amount: number;
  externalCompanyId: string;
  isEdited: boolean;
}

export interface JobExpense {
  id: number;
  title: string;
  type: ExpenseTabs;
  amount: string;
  category: string;
  expenseDate: string;
  reimburseTo: string;
  description: string;
  receiptUrl: string;
  receiptKey: string;
  receiptTitle: string;
  teamId: number;
  reimburseToMerchantName: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface GetJobExpensesPayload extends TeamIdPayload {
  jobId: string;
  type?: string;
  limit?: number;
  page?: number;
}

export interface GetJobExpensesResponse {
  data: {
    expenses: JobExpense[];
    totalExpenseAmount: string;
    teamExpenseAmount: string;
    companyExpenseAmount: string;
    count: number;
    isJobEditable: boolean;
  };
  meta: null;
}

export interface CreateJobExpensePayload {
  teamId: number;
  jobId: string;
  body: CreateJobExpenseRequest;
}

export interface CreateJobExpenseRequest {
  title: string;
  type: ExpenseTabs;
  amount: string;
  category: string;
  expenseDate: string;
  reimburseTo: string;
  description: string;
  receiptUrl?: string;
  receipttitle?: string;
}

export interface CreateJobExpenseResponse {
  data: {
    id: string;
    message: string;
  };
  meta: any;
}

export interface EditJobExpensePayload {
  teamId: number;
  jobId: string;
  expenseId: number;
  body: CreateJobExpenseRequest;
}

export interface DeleteJobExpensePayload {
  teamId: number;
  jobId: string;
  expenseId: number;
}

export interface UploadExpenseReceiptResponse {
  data: {
    imageUrl: string;
  };
  meta: null;
}

export interface UpdateJobExpenseReceiptPayload {
  teamId: number;
  jobId: string;
  expenseId: number;
  body: {
    receiptUrl: string;
  };
}

export interface UpdateJobExpenseReceiptResponse {
  data: {
    message: string;
  };
  meta: any;
}

export interface UpdateJobPaginationPayload extends TeamIdPayload {
  pageSize: number;
}

export interface UpdateJobPaginationResponse {
  data: {
    jobListPagination: number;
  };
  meta: any;
}

export interface UpdateJobGroupByStatusPayload extends TeamIdPayload {
  groupByStatus: boolean;
}

export interface UpdateJobGroupByStatusResponse {
  data: {
    groupByStatus: boolean;
  };
  meta: any;
}

export type GetJobsInitPayload = TeamIdPayload;

export interface GetJobsInitResponse {
  data: JobsInit;
  meta: any;
}

export interface JobsInit {
  jobInitialStatus: string;
  groupByStatus: boolean;
  jobListPagination: number;
}

export interface ServiceAreaSearchPayload {
  teamId: number;
  serviceAddressPoint: [number, number];
}

export interface ServiceAreaInfo {
  serviceAreaId: string;
  serviceAreaName: string;
  isEnclosing: boolean;
  distance: number;
}

export interface ServiceAreaSearchResponse {
  enclosingServiceAreas: ServiceAreaInfo[];
  nearestServiceAreas: ServiceAreaInfo[];
  defaultServiceAreas: ServiceAreaInfo[];
}

export interface EditJobServiceAreaPayload {
  teamId: number;
  jobId: string;
  serviceAreaId: string;
}

export interface EditJobServiceAreaResponse {
  data: {
    message: string;
    jobId: string;
  };
  meta: null;
}
